/**
 * AppShellError — full-page error state for the client-onboarding app.
 *
 * Used both when the initial application fetch fails (network / 500) and
 * when an application is found stuck in a non-terminal `submitted` state
 * (W6 synchronous-submit makes this a true error condition; legacy stuck
 * rows from the old async-decision flow surface here too).
 *
 * Props mirror the patterns already used inline in App.tsx — keeping the
 * surface compatible with the prior hand-rolled card so we extract once
 * and reuse without losing the existing visual.
 */

import type { ReactNode } from 'react';

export interface AppShellErrorProps {
  /** Bold-headline copy. Short. */
  readonly title: string;
  /** Body copy explaining what happened and what to do next. */
  readonly description: ReactNode;
  /**
   * Optional retry handler. When provided, renders a primary "Retry"
   * button. When omitted, no button is shown — useful for terminal
   * states ("contact support") where retry isn't actionable.
   */
  readonly onRetry?: () => void;
  /** Override the retry button label. */
  readonly retryLabel?: string;
}

export function AppShellError({
  title,
  description,
  onRetry,
  retryLabel = 'Retry',
}: AppShellErrorProps) {
  return (
    <div className="min-h-svh flex items-center justify-center bg-zinc-950">
      <div className="text-center space-y-6 max-w-md px-6">
        <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center mx-auto">
          <svg
            className="w-10 h-10 text-red-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
        </div>
        <div className="space-y-2">
          <h2 className="text-xl font-semibold text-white">{title}</h2>
          <div className="text-zinc-400">{description}</div>
        </div>
        {onRetry && (
          <button
            onClick={onRetry}
            className="inline-flex items-center justify-center px-6 py-3 bg-emerald-600 text-white font-medium rounded-lg hover:bg-emerald-700 transition-colors shadow-sm"
          >
            {retryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
